import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Alert
} from '@mui/material';
import { CSVFileIcon } from 'icons/fileTypeIcons';

// File uploader component - NO HEADER
const FileUploader = ({ onFileSelect }) => {
  const [dragActive, setDragActive] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragActive) setDragActive(true);
  };

  const isValidImportFile = (file) => {
    const validExtensions = [
      '.csv',
      '.xlsx',
      '.xls',
      '.ods',
      '.txt'
    ];

    const validMimeTypes = [
      'text/csv',
      'application/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.oasis.opendocument.spreadsheet',
      'text/plain',
      'application/octet-stream'
    ];

    const fileName = file.name.toLowerCase();
    const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));
    const hasValidMimeType = validMimeTypes.includes(file.type) || file.type === '';

    return hasValidExtension || hasValidMimeType;
  };

  const processFile = (file) => {
    if (!file) {
      showErrorMessage('No file selected');
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      showErrorMessage('File size exceeds 50MB limit');
      return;
    }

    if (!isValidImportFile(file)) {
      showErrorMessage(`File "${file.name}" is not a supported file type. Please upload CSV or Excel files.`);
      return;
    }

    onFileSelect(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer.files;
    if (files.length) {
      processFile(files[0]);
    }
  };

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file) {
      processFile(file);
    }
  };

  const showErrorMessage = (message) => {
    setErrorMessage(message);
    setShowError(true);
  };

  const handleCloseError = () => {
    setShowError(false);
  };

  return (
    <>
      <Box
        sx={{
          width: '100%',
          height: '290px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#ffffff',
          borderRadius: '18px',
          border: dragActive ? '2px dashed #29AEF8' : '2px dashed #E0E0E0',
          cursor: 'pointer',
          transition: 'all 0.2s',
          '&:hover': {
            borderColor: '#29AEF8',
            backgroundColor: 'rgba(41, 174, 248, 0.02)'
          }
        }}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        component="label"
      >
        <input
          type="file"
          accept=".csv,.xlsx,.xls,.ods,.txt,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.oasis.opendocument.spreadsheet,text/plain,application/octet-stream"
          onChange={handleFileInput}
          style={{ display: 'none' }}
        />
        <Box sx={{ textAlign: 'center' }}>
          <Box
            sx={{
              width: '80px',
              height: '80px',
              backgroundColor: '#F7F7F7',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}
          >
            <CSVFileIcon sx={{ fontSize: 40, color: '#4F4F4F' }} />
          </Box>
          <Typography variant="h6" sx={{ mb: 1, color: '#000000', fontSize: '24px', fontWeight: 600 }}>
            <span style={{ color: '#29AEF8' }}>Select CSV or Excel file</span> to upload
          </Typography>
          <Typography sx={{ color: '#4F4F4F', fontSize: '16px', fontWeight: 500 }}>
            or drag and drop it here
          </Typography>
          <Typography sx={{ color: '#828282', fontSize: '13px', mt: 3 }}>
            Must be <b>.csv, .xlsx, .xls</b> file with maximum file size <b>50MB</b>
          </Typography>
        </Box>
      </Box>

      {showError && (
        <Alert
          severity="error"
          onClose={handleCloseError}
          sx={{ mt: 3 }}
        >
          {errorMessage}
        </Alert>
      )}
    </>
  );
};

FileUploader.propTypes = {
  onFileSelect: PropTypes.func.isRequired
};

export default FileUploader;